/**
 * 依頼書（仕入先共有）用の純関数。
 * 分納行展開・選択可否・帳票ビューモデル組み立て。
 */

import { fingerprintFromShareRow } from './destinationFingerprint';
import { isSingleDateShukkaType } from './shukkaDateRules';

export const MIXED_LABEL = '（明細により異なる）';

/** YYYY-MM-DD に正規化（比較用）。日付でない場合は null */
export function normalizeDateKey(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
}

export function candidateKey(hacchuCode, hacchuDatacode, splitNo = 0) {
  return `${hacchuCode}-${hacchuDatacode}-${splitNo || 0}`;
}

function displayOrEmpty(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

/**
 * 仕入先共有明細配列を依頼書選択候補（分納行／明細行）に展開する。
 * @param {Array} items SupplierShareItemResponse[]
 * @returns {Array} candidates
 */
export function expandRequestFormCandidates(items) {
  if (!Array.isArray(items)) return [];
  const out = [];

  for (const item of items) {
    if (!item) continue;
    const splits = Array.isArray(item.split_plans) ? item.split_plans : [];
    const hasSplits = splits.length > 0;

    if (hasSplits) {
      for (const sp of splits) {
        const qty = sp.planned_qty != null ? Number(sp.planned_qty) : null;
        const weight =
          item.product_weight != null && qty != null
            ? Number(item.product_weight) * qty
            : null;
        out.push({
          key: candidateKey(item.hacchu_code, item.hacchu_datacode, sp.split_no),
          hacchu_code: item.hacchu_code,
          hacchu_changecnt: item.hacchu_changecnt,
          hacchu_datacode: item.hacchu_datacode,
          split_no: sp.split_no,
          is_split: true,
          shipment_date: sp.shipment_date ?? null,
          arrival_date: sp.arrival_date ?? null,
          supplier_response_due_date: sp.supplier_response_due_date ?? null,
          qty,
          tani: item.tani,
          line_weight: weight,
          product_weight: item.product_weight ?? null,
          latest_ship_request_issued_at: item.latest_ship_request_issued_at ?? null,
          latest_ship_request_issued_by: item.latest_ship_request_issued_by ?? null,
          product_disp: item.product_disp,
          product_name: item.product_name,
          product_size: item.product_size,
          product_spec: item.product_spec,
          supplier_stock_flag: !!item.supplier_stock_flag,
          shukka_type: item.shukka_type,
          hacchu_date: item.hacchu_date,
          hacchu_no: item.hacchu_no,
          juchu_no: item.juchu_no,
          hacchu_shain_name: item.hacchu_shain_name,
          tanto_shain_name: item.tanto_shain_name,
          customer_name: item.customer_name,
          nonyusaki: item.nonyusaki,
          genba_name: item.genba_name,
          supplier_name: item.supplier_name,
          juchu_direct_name: item.juchu_direct_name,
          juchu_direct_address: item.juchu_direct_address,
          juchu_direct_tel: item.juchu_direct_tel,
          juchu_biko: item.juchu_biko,
          supplier_share_memo: item.supplier_share_memo,
          juchu_code: item.juchu_code,
          juchu_edaban: item.juchu_edaban,
        });
      }
    } else {
      const qty = item.hacchu_amount != null ? Number(item.hacchu_amount) : null;
      const weight =
        item.product_weight != null && qty != null
          ? Number(item.product_weight) * qty
          : item.total_weight != null
            ? Number(item.total_weight)
            : null;
      out.push({
        key: candidateKey(item.hacchu_code, item.hacchu_datacode, 0),
        hacchu_code: item.hacchu_code,
        hacchu_changecnt: item.hacchu_changecnt,
        hacchu_datacode: item.hacchu_datacode,
        split_no: 0,
        is_split: false,
        shipment_date: item.shipment_date ?? null,
        arrival_date: item.arrival_date ?? null,
        supplier_response_due_date: item.supplier_response_due_date ?? null,
        qty,
        tani: item.tani,
        line_weight: weight,
        product_weight: item.product_weight ?? null,
        latest_ship_request_issued_at: item.latest_ship_request_issued_at ?? null,
        latest_ship_request_issued_by: item.latest_ship_request_issued_by ?? null,
        product_disp: item.product_disp,
        product_name: item.product_name,
        product_size: item.product_size,
        product_spec: item.product_spec,
        supplier_stock_flag: !!item.supplier_stock_flag,
        shukka_type: item.shukka_type,
        hacchu_date: item.hacchu_date,
        hacchu_no: item.hacchu_no,
        juchu_no: item.juchu_no,
        hacchu_shain_name: item.hacchu_shain_name,
        tanto_shain_name: item.tanto_shain_name,
        customer_name: item.customer_name,
        nonyusaki: item.nonyusaki,
        genba_name: item.genba_name,
        supplier_name: item.supplier_name,
        juchu_direct_name: item.juchu_direct_name,
        juchu_direct_address: item.juchu_direct_address,
        juchu_direct_tel: item.juchu_direct_tel,
        juchu_biko: item.juchu_biko,
        supplier_share_memo: item.supplier_share_memo,
        juchu_code: item.juchu_code,
        juchu_edaban: item.juchu_edaban,
      });
    }
  }

  return out;
}

/**
 * 送り先が同じかたまりとして選べるか。
 * 空送り先どうしはまとめない（同一枝番のみ可）。
 */
export function isSameDestinationForRequestForm(anchorCandidate, candidate) {
  if (!anchorCandidate || !candidate) return false;
  const aFp = fingerprintFromShareRow(anchorCandidate);
  const cFp = fingerprintFromShareRow(candidate);
  if (aFp == null || cFp == null) {
    return (
      anchorCandidate.juchu_code != null &&
      candidate.juchu_code != null &&
      anchorCandidate.juchu_code === candidate.juchu_code &&
      String(anchorCandidate.juchu_edaban) === String(candidate.juchu_edaban)
    );
  }
  return aFp === cFp;
}

/**
 * 候補行の選択可否と理由。
 * @param {object|null} anchorCandidate 既に選択されている基準行（発送日・送り先の両方の基準）
 * @returns {{ selectable: boolean, reason: string|null }}
 */
export function getCandidateSelectability(candidate, anchorShipmentDateKey = null, anchorCandidate = null) {
  if (!candidate) {
    return { selectable: false, reason: 'データなし' };
  }
  const dateKey = normalizeDateKey(candidate.shipment_date);
  if (!dateKey) {
    return { selectable: false, reason: '発送日未設定' };
  }
  if (anchorShipmentDateKey && dateKey !== anchorShipmentDateKey) {
    return { selectable: false, reason: '発送日が違う' };
  }
  if (anchorCandidate && !isSameDestinationForRequestForm(anchorCandidate, candidate)) {
    return { selectable: false, reason: '送り先が違う' };
  }
  return { selectable: true, reason: null };
}

/**
 * 選択集合に対し、同一発送日・同一送り先制約を適用した新しい選択キー Set を返す。
 */
export function constrainSelectionToSameShipmentDate(candidates, selectedKeys) {
  const byKey = new Map(candidates.map((c) => [c.key, c]));
  const next = new Set();
  let anchorDate = null;
  let anchorCand = null;

  for (const key of selectedKeys) {
    const c = byKey.get(key);
    if (!c) continue;
    const { selectable } = getCandidateSelectability(c, anchorDate, anchorCand);
    if (!selectable) continue;
    if (!anchorDate) {
      anchorDate = normalizeDateKey(c.shipment_date);
      anchorCand = c;
    }
    next.add(key);
  }
  return { selectedKeys: next, anchorShipmentDateKey: anchorDate, anchorCandidate: anchorCand };
}

/**
 * 基準発送日・送り先の選択可能行をすべて返す（全選択用）。
 */
export function keysForShipmentDate(candidates, shipmentDateKey, anchorCandidate = null) {
  if (!shipmentDateKey) return [];
  return candidates
    .filter((c) => {
      const { selectable } = getCandidateSelectability(c, shipmentDateKey, anchorCandidate);
      return selectable && normalizeDateKey(c.shipment_date) === shipmentDateKey;
    })
    .map((c) => c.key);
}

function uniqueOrMixed(values) {
  const normalized = values.map((v) => displayOrEmpty(v));
  const nonempty = normalized.filter((v) => v !== '');
  if (nonempty.length === 0) {
    return { value: '', mixed: false, empty: true };
  }
  const uniq = [...new Set(normalized)];
  // 空と値の混在も「異なる」扱い
  if (uniq.length === 1) {
    return { value: uniq[0], mixed: false, empty: uniq[0] === '' };
  }
  return { value: MIXED_LABEL, mixed: true, empty: false };
}

function dash(v) {
  const s = displayOrEmpty(v);
  return s === '' ? '-' : s;
}

/** 依頼書着日欄の行ごとの表示値（単一日付なら出荷形態、否则着日キー） */
export function requestFormArrivalCellValue(row) {
  if (isSingleDateShukkaType(row?.shukka_type)) {
    const t = displayOrEmpty(row.shukka_type);
    return t === '' ? '-' : t;
  }
  return normalizeDateKey(row?.arrival_date) || '';
}

/**
 * 選択行から帳票ビューモデルを組み立てる。
 * @param {Array} selectedRows expand 済み候補のうち選択分
 */
export function buildRequestFormViewModel(selectedRows) {
  const rows = Array.isArray(selectedRows) ? selectedRows : [];
  if (rows.length === 0) {
    return null;
  }

  const first = rows[0];
  const shipmentDateKey = normalizeDateKey(first.shipment_date);

  const shukka = uniqueOrMixed(rows.map((r) => r.shukka_type));
  const arrivalCells = uniqueOrMixed(rows.map((r) => requestFormArrivalCellValue(r)));
  const nonyusaki = uniqueOrMixed(rows.map((r) => r.nonyusaki));
  const genba = uniqueOrMixed(rows.map((r) => r.genba_name));
  const directName = uniqueOrMixed(rows.map((r) => r.juchu_direct_name));
  const directAddr = uniqueOrMixed(rows.map((r) => r.juchu_direct_address));
  const directTel = uniqueOrMixed(rows.map((r) => r.juchu_direct_tel));
  const biko = uniqueOrMixed(rows.map((r) => r.juchu_biko));
  const memo = uniqueOrMixed(rows.map((r) => r.supplier_share_memo));
  const customer = uniqueOrMixed(rows.map((r) => r.customer_name));

  const showDetailArrival = arrivalCells.mixed;
  const showDetailShukka = shukka.mixed;
  const showDetailNonyusaki = nonyusaki.mixed;
  const showDetailDestination = directName.mixed || directAddr.mixed;

  let totalWeight = 0;
  let hasAnyWeight = false;
  for (const r of rows) {
    if (r.line_weight != null && !Number.isNaN(Number(r.line_weight))) {
      totalWeight += Number(r.line_weight);
      hasAnyWeight = true;
    }
  }

  const detailLines = rows.map((r, idx) => ({
    no: idx + 1,
    key: r.key,
    product_disp: dash(r.product_disp),
    product_name: dash(r.product_name),
    product_size: dash(r.product_size),
    qty: r.qty != null ? r.qty : null,
    tani: r.tani || '',
    product_weight: r.product_weight,
    line_weight: r.line_weight,
    supplier_name: dash(r.supplier_name),
    product_spec: dash(r.product_spec),
    shukka_type: dash(r.shukka_type),
    arrival_date: normalizeDateKey(r.arrival_date),
    arrival_cell: requestFormArrivalCellValue(r) || '-',
    nonyusaki: dash(r.nonyusaki),
    juchu_direct_name: dash(r.juchu_direct_name),
    juchu_direct_address: dash(r.juchu_direct_address),
    juchu_direct_tel: dash(r.juchu_direct_tel),
    juchu_biko: dash(r.juchu_biko),
    supplier_share_memo: dash(r.supplier_share_memo),
  }));

  const footerBiko = biko.mixed
    ? rows.map((r, i) => ({ no: i + 1, text: dash(r.juchu_biko) }))
    : null;
  const footerMemo = memo.mixed
    ? rows.map((r, i) => ({ no: i + 1, text: dash(r.supplier_share_memo) }))
    : null;

  return {
    title: '出荷依頼書',
    header: {
      shukka_type: shukka.mixed ? MIXED_LABEL : dash(shukka.value),
      juchu_no: dash(first.juchu_no),
      hacchu_no: dash(first.hacchu_no),
      tanto_shain_name: dash(first.tanto_shain_name),
      issued_date: normalizeDateKey(first.latest_ship_request_issued_at),
      issued_by: dash(first.latest_ship_request_issued_by),
      customer_name: customer.mixed ? MIXED_LABEL : dash(customer.value),
      nonyusaki: nonyusaki.mixed ? MIXED_LABEL : dash(nonyusaki.value),
      genba_name: genba.mixed ? MIXED_LABEL : dash(genba.value),
      shipment_date: shipmentDateKey,
      arrival_date: arrivalCells.mixed
        ? MIXED_LABEL
        : arrivalCells.empty
          ? '-'
          : arrivalCells.value,
      total_weight: hasAnyWeight ? totalWeight : null,
    },
    destination: {
      name: directName.mixed ? MIXED_LABEL : dash(directName.value),
      address: directAddr.mixed ? MIXED_LABEL : dash(directAddr.value),
      tel: directTel.mixed ? MIXED_LABEL : dash(directTel.value),
    },
    flags: {
      showDetailArrival,
      showDetailShukka,
      showDetailNonyusaki,
      showDetailDestination,
      bikoMixed: biko.mixed,
      memoMixed: memo.mixed,
    },
    detailLines,
    footer: {
      biko: biko.mixed ? null : dash(biko.value),
      bikoLines: footerBiko,
      memo: memo.mixed ? null : dash(memo.value),
      memoLines: footerMemo,
    },
  };
}
